import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { useLocation, useSearchParams } from 'react-router'

import { Footer } from '@/components/shared/footer'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { ActionSection, RadioSection } from '@/components/ui/custom-dropdown'
import { useLocalProgress } from '@/hooks/useLocalProgress'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { useNextEpisodeTiming } from '@/hooks/useNextEpisodeTiming'
import { useOfflineUrl } from '@/hooks/useOfflineUrl'
import { isRestoredRoute } from '@/hooks/useRouteRestore'
import { useVideoQuality } from '@/hooks/useVideoQuality'
import { FOOTER_HEIGHT, HEADER_HEIGHT } from '@/lib/constants'
import {
  computeNextPreviewThresholds,
  type NextPreviewStage,
  resolveNextPreviewStage,
  sampleFromDismiss,
  sampleFromNextPress,
} from '@/lib/next-episode-timing'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { useBufferHold } from '../hooks/useBufferHold'
import { useOrientation } from '../hooks/useOrientation'
import { SWIPE_BACK_ANIMATION_MS, useSwipeBack } from '../hooks/useSwipeBack'
import { NextEpisodePreview } from '../shared/next-episode-preview'
import { BufferingOverlay } from './buffering-overlay'
import { VideoControls } from './video-controls'

// エピソードごとにプレイヤーが作り直されるため、前後のエピソードへ移るときだけ最大化状態を引き継ぐ
let carryOverMaximized = false

/** 復元先へ到達したとみなす誤差（秒） */
const SEEK_MATCH_TOLERANCE_SEC = 1

/** play() が実際に開始されたかを確認するまでの待ち時間（ms） */
const PLAY_START_CHECK_MS = 1000

/** 画質の高い順 */
const QUALITY_RANK = ['original', '720p', '480p']

/** ヒットテスト更新のために 1px スクロールしたままにしておく時間（ms） */
const HIT_TEST_RESTORE_MS = 100
/** 横向きになってからヒットテスト更新をやり直すタイミング（ms） */
const HIT_TEST_RETRY_MS = [300, 800]
/** 最後の resize からヒットテスト更新まで待つ時間（ms） */
const HIT_TEST_SETTLE_MS = 150

/** MediaError.code ごとの表示メッセージ */
const DEFAULT_MEDIA_ERROR_MESSAGE = '動画を読み込めませんでした'
const MEDIA_ERROR_MESSAGES = new Map<number, string>([
  [1, '動画の読み込みが中断されました'],
  [2, 'ネットワークエラーで動画を読み込めませんでした'],
  [3, '動画のデコードに失敗しました'],
  [4, '動画を取得できませんでした（URLの有効期限切れの可能性）'],
])

type Props = {
  url: string
  onClose: () => void
  title: string
  onInfoClick?: () => void
  onNext?: () => void
  onPrev?: () => void
  nextEpisodeId?: string
  nextEpisodeTitle?: string
  episodeId?: number
  productId?: number
}

const PROGRESS_SAVE_DEBOUNCE = 2000 // 2秒間隔で保存

export const VideoPlayer = ({
  url,
  onClose,
  title,
  onInfoClick,
  onNext,
  onPrev,
  nextEpisodeId,
  nextEpisodeTitle,
  episodeId,
  productId,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  // 再生速度の変更などで state は消えるため、開いた時点の値を保持する
  const [openedByRouteRestore] = useState(() => isRestoredRoute(location.state))
  const isLandscape = useOrientation()

  // 画質選択（自動切替なし / 常に手動選択）
  const storedQuality = useUISettingsStore((s) => s.videoQuality)
  const setStoredQuality = useUISettingsStore((s) => s.setVideoQuality)
  const [selectedQuality, setSelectedQuality] = useState(storedQuality)
  const { isWifi } = useNetworkStatus()
  const { options: qualityOptions, conversion: qualityConversion, ensureQuality } = useVideoQuality(episodeId ?? null)

  // 画質付きのストリームパス（original はクエリなし）
  const streamPath = selectedQuality === 'original' ? url : `${url}?quality=${encodeURIComponent(selectedQuality)}`

  // オフラインBlobがあればObjectURLを、なければ署名付きURLを使用
  const {
    url: directVideoUrl,
    isLoading: isUrlLoading,
    isOffline,
    refetch: refetchVideoUrl,
  } = useOfflineUrl(episodeId ?? null, streamPath)

  // URL を安定化: 同じ画質の間は一度取得した URL を変更しない（再フェッチによる重複再生を防止）
  // ただし読み込みに失敗した URL は例外で、新しい URL が来たら差し替える
  const failedUrlRef = useRef<string | null>(null)
  const [stableSource, setStableSource] = useState<{ url: string; quality: string } | null>(() =>
    directVideoUrl ? { quality: selectedQuality, url: directVideoUrl } : null,
  )
  useEffect(() => {
    if (!directVideoUrl) return
    if (stableSource && stableSource.quality === selectedQuality && stableSource.url !== failedUrlRef.current) return
    setStableSource({ quality: selectedQuality, url: directVideoUrl })
  }, [directVideoUrl, selectedQuality, stableSource])
  const stableVideoUrl = stableSource?.url ?? null

  // 再試行カウンタ。URL にクエリを足して別リソース扱いにすることで、
  // 壊れた Service Worker キャッシュや失敗した接続を確実に捨てて取り直す
  const [reloadToken, setReloadToken] = useState(0)
  // src に #t= として埋め込む開始位置（Media Fragment URI）。
  // ブラウザによっては loadedmetadata 後のプログラマチックな seek を取りこぼし、
  // UI 上の位置に関わらずデコーダが 0 から再生してしまうため URL 側でも位置を伝える
  const [srcSeek, setSrcSeek] = useState<number | null>(null)
  const videoSrc = useMemo(() => {
    if (!stableVideoUrl) return null
    // ObjectURL(blob:) はクエリやフラグメントを付けると無効になるためそのまま使う
    if (stableVideoUrl.startsWith('blob:')) return stableVideoUrl
    const query = reloadToken === 0 ? '' : `${stableVideoUrl.includes('?') ? '&' : '?'}_retry=${reloadToken}`
    const fragment = srcSeek && srcSeek > 0 ? `#t=${srcSeek}` : ''
    return `${stableVideoUrl}${query}${fragment}`
  }, [stableVideoUrl, reloadToken, srcSeek])

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [nextPreviewStage, setNextPreviewStage] = useState<NextPreviewStage | null>(null)
  const [nextEpisodePreviewDismissed, setNextEpisodePreviewDismissed] = useState(false)
  const volume = parseFloat(searchParams.get('volume') ?? '0.8')
  const [playbackRate, setPlaybackRate] = useState(() => {
    const r = searchParams.get('playbackRate')
    return r ? parseFloat(r) : 1
  })
  const [controlsVisible, setControlsVisible] = useState(false)
  const [isBuffering, setIsBuffering] = useState(true)
  // ブラウザの全画面 API は使わず、コンテナを画面いっぱいに広げて自前のオーバーレイを見せ続ける
  const [isMaximized, setIsMaximized] = useState(() => carryOverMaximized)

  useEffect(() => {
    if (!isMaximized) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMaximized(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMaximized])

  useEffect(() => {
    carryOverMaximized = false
  }, [])

  const goNext = useMemo(
    () =>
      onNext &&
      (() => {
        carryOverMaximized = isMaximized
        onNext()
      }),
    [onNext, isMaximized],
  )
  const goPrev = useMemo(
    () =>
      onPrev &&
      (() => {
        carryOverMaximized = isMaximized
        onPrev()
      }),
    [onPrev, isMaximized],
  )

  const [hasLoadError, setHasLoadError] = useState(false)
  const [errorDetail, setErrorDetail] = useState(DEFAULT_MEDIA_ERROR_MESSAGE)
  const [bufferProgress, setBufferProgress] = useState<{ loaded: number; total: number } | null>(null)

  const playerRef = useRef<HTMLVideoElement | null>(null)
  const lastSavedProgressRef = useRef<number>(0)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 適用したい復元位置。loadedmetadata / canplay / ready の各タイミングで適用し、
  // 到達を確認できたら null にする（seek は取りこぼされることがある）
  const pendingSeekRef = useRef<number | null>(null)
  // 直近の再生位置。メディアリソースが破棄されたあとの復帰に使う
  const lastKnownTimeRef = useRef<number>(0)
  // 自動再生済みフラグ（メディアリソース再ロード時に勝手に再生し直さないため）
  const hasAutoPlayedRef = useRef(false)
  // このエピソードを読了にしたか（以降の進捗保存で未読了に戻さないため）
  const hasMarkedCompletedRef = useRef(false)
  const [isPlayerReady, setIsPlayerReady] = useState(false)
  const [hasRestoredProgress, setHasRestoredProgress] = useState(false)
  // 画質切替時に復元する再生位置と再生状態
  const qualitySwitchResumeRef = useRef<{ time: number; playing: boolean } | null>(null)

  const { learnedRemainingSec, recordSample } = useNextEpisodeTiming(productId)
  const nextPreviewThresholds = useMemo(
    () => computeNextPreviewThresholds(duration, learnedRemainingSec),
    [duration, learnedRemainingSec],
  )

  // 手動で「次へ」を押したときだけ、押した位置を作品ごとの表示タイミングとして学習する
  // （再生終了による自動遷移は見続けただけで手がかりにならないため goNext を直接使う）
  const goNextManual = useMemo(
    () =>
      goNext &&
      (() => {
        const currentSeconds = playerRef.current?.currentTime ?? lastKnownTimeRef.current
        const sample = sampleFromNextPress(duration - currentSeconds, duration, nextPreviewStage !== null)
        if (sample !== null) recordSample(sample)
        goNext()
      }),
    [goNext, duration, nextPreviewStage, recordSample],
  )

  // ローカルファースト進捗管理（オフライン対応）
  const {
    progress: localProgress,
    isLoading: isProgressLoading,
    saveProgress: saveLocalProgress,
    markCompleted,
  } = useLocalProgress(episodeId ?? null)

  const { isHolding, holdingRef, holdIfShortOnPlaying, getDownloadRate } = useBufferHold({
    videoRef: playerRef,
    playing,
    playbackRate,
    onResumeFailed: () => setPlaying(false),
  })

  // iOS(WKWebView) のバグ回避: 回転後に position: fixed 要素のヒットテスト領域が
  // 古いまま残り、描画位置とタップ判定位置がズレる。スクロールが発生すると
  // 再計算されるため、1px だけ動かして戻す（overflow / bodyをfixed にする
  // スクロールロックや、ネスト fixed の解消では解決しないことを確認済み）。
  useEffect(() => {
    if (!isLandscape) return

    const root = document.documentElement
    const originalMinHeight = root.style.minHeight
    let baseY: number | null = null
    let restoreTimer: ReturnType<typeof setTimeout> | undefined
    let settleTimer: ReturnType<typeof setTimeout> | undefined

    const restore = () => {
      if (baseY !== null) window.scrollTo(0, baseY)
      baseY = null
      root.style.minHeight = originalMinHeight
    }

    const refreshHitTest = () => {
      clearTimeout(restoreTimer)
      // ??= は React Compiler が未対応で、コンポーネント全体の最適化が外れるため使わない
      if (baseY === null) baseY = window.scrollY
      // 一覧から開いたときなど文書がスクロールできないと scrollTo が空振りするため、一時的に 1px 伸ばす
      root.style.minHeight = `${window.innerHeight + 1}px`
      window.scrollTo(0, baseY + 1)
      // 同じフレームで戻すとスクロールが確定せず、判定領域が更新されないことがある
      restoreTimer = setTimeout(restore, HIT_TEST_RESTORE_MS)
    }

    // 回転のアニメーション中は resize が続き、最後の配置でまたずれるため落ち着いてからも行う
    const handleResize = () => {
      clearTimeout(settleTimer)
      settleTimer = setTimeout(refreshHitTest, HIT_TEST_SETTLE_MS)
    }

    refreshHitTest()
    const retryTimers = HIT_TEST_RETRY_MS.map((ms) => setTimeout(refreshHitTest, ms))
    window.addEventListener('resize', handleResize)
    window.visualViewport?.addEventListener('resize', handleResize)

    return () => {
      retryTimers.forEach(clearTimeout)
      clearTimeout(settleTimer)
      clearTimeout(restoreTimer)
      window.removeEventListener('resize', handleResize)
      window.visualViewport?.removeEventListener('resize', handleResize)
      restore()
    }
  }, [isLandscape])

  // マウント時: DOM 上に残っている全 video 要素を停止（ゴースト要素対策）
  useEffect(() => {
    const videos = document.querySelectorAll('video')
    videos.forEach((v) => {
      if (!v.paused) {
        v.pause()
      }
    })
  }, [episodeId])

  // 再生開始時に他の video 要素が再生中なら停止する（重複再生防止）
  useEffect(() => {
    if (!playing) return
    const videos = document.querySelectorAll('video')
    const current = playerRef.current
    videos.forEach((v) => {
      if (v !== current && !v.paused) {
        v.pause()
        v.removeAttribute('src')
        v.load()
      }
    })
  }, [playing])

  /** 復元したい位置を video 要素へ適用する（到達済みなら pending を解除するだけ） */
  const applyPendingSeek = useCallback(() => {
    const video = playerRef.current
    const target = pendingSeekRef.current
    if (!video || target === null || target <= 0) return
    if (!video.duration || Number.isNaN(video.duration)) return
    if (Math.abs(video.currentTime - target) < SEEK_MATCH_TOLERANCE_SEC) {
      pendingSeekRef.current = null
      return
    }
    video.currentTime = target
  }, [])

  /** seek 完了を見て復元の達成を確定する */
  const handleSeeked = useCallback(() => {
    const video = playerRef.current
    if (!video) return
    const target = pendingSeekRef.current
    if (target !== null && Math.abs(video.currentTime - target) < SEEK_MATCH_TOLERANCE_SEC) {
      pendingSeekRef.current = null
    }
    lastKnownTimeRef.current = video.currentTime
    // 一時停止中の seek では playing イベントが来ないため、ここでも表示を解除する
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsBuffering(false)
    }
  }, [])

  // 一時停止したまま放置するとブラウザがメディアリソースを破棄することがあり、
  // 再ロードされると先頭に巻き戻る。直前の位置を復元対象として覚えておく
  const handleEmptied = useCallback(() => {
    if (lastKnownTimeRef.current > 0) {
      pendingSeekRef.current = lastKnownTimeRef.current
    }
  }, [])

  // プレーヤー準備完了時
  const handleReady = useCallback(() => {
    setIsPlayerReady(true)
    setIsBuffering(false)
    setHasLoadError(false)
    // 画質切替・再試行直後は切替前の再生位置・再生状態を復元する
    const resume = qualitySwitchResumeRef.current
    if (resume) {
      qualitySwitchResumeRef.current = null
      pendingSeekRef.current = null
      if (playerRef.current) {
        playerRef.current.currentTime = resume.time
      }
      lastKnownTimeRef.current = resume.time
      lastSavedProgressRef.current = resume.time
      setPlaying(resume.playing)
      return
    }
    applyPendingSeek()
    // 自動再生は初回のみ（メディアリソースの再ロードで一時停止中に再生し直さない）
    if (hasAutoPlayedRef.current) return
    hasAutoPlayedRef.current = true
    // アプリ再起動時のルート復元で開かれたときは勝手に再生しない
    if (openedByRouteRestore) return
    setPlaying(true)
  }, [applyPendingSeek, openedByRouteRestore])

  // 読み込み失敗時: バッファリング表示のまま固まらないようエラーを提示する
  const handleError = useCallback(() => {
    setIsBuffering(false)
    setPlaying(false)
    setHasLoadError(true)
    setErrorDetail(MEDIA_ERROR_MESSAGES.get(playerRef.current?.error?.code ?? 0) ?? DEFAULT_MEDIA_ERROR_MESSAGE)
  }, [])

  /** 再生位置を保ったまま読み込みし直す */
  const handleRetry = useCallback(() => {
    qualitySwitchResumeRef.current = { playing: true, time: playerRef.current?.currentTime || currentTime }
    setHasLoadError(false)
    setIsBuffering(true)
    // 失敗した URL を記録して、署名付きURLを取り直したときに差し替えられるようにする
    failedUrlRef.current = stableVideoUrl
    refetchVideoUrl()
    // src を変えて別リソースとして取り直す（load() だけだと壊れたキャッシュを引き続き掴む）
    setReloadToken((token) => token + 1)
  }, [currentTime, refetchVideoUrl, stableVideoUrl])

  /** 画質変更：未生成なら変換完了を待ってから切り替える */
  const handleQualityChange = useCallback(
    async (quality: string) => {
      if (quality === selectedQuality) return

      const available = await ensureQuality(quality)
      if (!available) return

      qualitySwitchResumeRef.current = {
        playing,
        time: playerRef.current?.currentTime ?? currentTime,
      }
      setIsPlayerReady(false)
      setIsBuffering(true)
      setStoredQuality(quality)
      setSelectedQuality(quality)
    },
    [currentTime, ensureQuality, playing, selectedQuality, setStoredQuality],
  )

  // 前回選んだ画質がこのエピソードで未生成なら原本にフォールバックする
  // （サーバー側も原本を返すため、表示と実際の配信を一致させる）
  useEffect(() => {
    if (qualityOptions.length === 0 || selectedQuality === 'original') return
    const option = qualityOptions.find((o) => o.quality === selectedQuality)
    if (!option || option.status !== 'ready') setSelectedQuality('original')
  }, [qualityOptions, selectedQuality])

  // 進捗復元: 進捗のロード完了時点で復元位置を確定する。
  // src の #t= と loadedmetadata / canplay での seek の両方で適用する
  useEffect(() => {
    if (isProgressLoading || hasRestoredProgress) return
    setHasRestoredProgress(true)
    const saved = localProgress?.progress ?? 0
    if (saved <= 0 || localProgress?.is_completed) return
    pendingSeekRef.current = saved
    lastKnownTimeRef.current = saved
    lastSavedProgressRef.current = saved
    setSrcSeek(saved)
  }, [localProgress, isProgressLoading, hasRestoredProgress])

  const handleBuffer = useCallback(() => {
    setIsBuffering(true)
  }, [])

  const handleBufferEnd = useCallback(() => {
    setIsBuffering(false)
    setHasLoadError(false)
    // 同じイベント内で切り替えるため、貯め待ちに入っても表示が途切れない
    holdIfShortOnPlaying()
  }, [holdIfShortOnPlaying])

  /** 再生可能な状態まで読み込めた時点で復元位置を適用しつつバッファ表示を解除する */
  const handleCanPlay = useCallback(() => {
    applyPendingSeek()
    setIsBuffering(false)
  }, [applyPendingSeek])

  // play() が拒否される（iOS の自動再生制限など）と要素は一時停止のままなのに
  // React 側は再生中のままになり、バッファリング表示が消えず操作もできなくなる。
  // 実際に再生が始まったかを確認し、始まっていなければ停止状態へ戻す
  useEffect(() => {
    if (!playing) return
    const video = playerRef.current
    if (!video) return
    const timer = setTimeout(() => {
      if (!video.paused) return
      setPlaying(false)
      setIsBuffering(false)
    }, PLAY_START_CHECK_MS)
    return () => clearTimeout(timer)
  }, [playing])

  // ネイティブ <video> 要素のバッファー進捗を監視
  useEffect(() => {
    if (!stableVideoUrl) return

    let videoEl: HTMLVideoElement | null = null
    let intervalId: ReturnType<typeof setInterval> | null = null

    const handleNativeProgress = () => {
      if (videoEl && videoEl.buffered.length > 0 && videoEl.duration > 0) {
        const bufferedEnd = videoEl.buffered.end(videoEl.buffered.length - 1)
        setBufferProgress({
          loaded: bufferedEnd,
          total: videoEl.duration,
        })
      }
    }

    const attachListeners = () => {
      const player = playerRef.current
      if (!player || !('buffered' in player)) return false

      videoEl = player
      videoEl.addEventListener('progress', handleNativeProgress)
      videoEl.addEventListener('loadeddata', handleNativeProgress)
      handleNativeProgress()
      return true
    }

    if (!attachListeners()) {
      intervalId = setInterval(() => {
        if (attachListeners() && intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      }, 100)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
      if (videoEl) {
        videoEl.removeEventListener('progress', handleNativeProgress)
        videoEl.removeEventListener('loadeddata', handleNativeProgress)
      }
    }
  }, [stableVideoUrl])

  const handleSeek = useCallback((seconds: number) => {
    // 手動シーク後に復元位置へ巻き戻さないよう pending を破棄する
    pendingSeekRef.current = null
    lastKnownTimeRef.current = seconds
    if (playerRef.current) {
      playerRef.current.currentTime = seconds
    }
    setCurrentTime(seconds)
  }, [])

  const handleSeekPreview = useCallback((seconds: number) => {
    // シーク中のプレビュー（currentTime は更新しない）
    pendingSeekRef.current = null
    lastKnownTimeRef.current = seconds
    if (playerRef.current) {
      playerRef.current.currentTime = seconds
    }
  }, [])

  const saveProgress = useCallback(
    async (progressSeconds: number, totalSeconds: number, incrementPlayCount = false) => {
      if (!episodeId || totalSeconds <= 0) return

      const isCompleted = progressSeconds >= totalSeconds - 1
      if (isCompleted || incrementPlayCount) {
        hasMarkedCompletedRef.current = true
        await markCompleted()
        return
      }
      // 読了にした直後は遷移時の flush などで再生位置が書き戻され、未読了に戻ってしまう
      if (hasMarkedCompletedRef.current) return
      await saveLocalProgress(Math.floor(progressSeconds))
    },
    [episodeId, saveLocalProgress, markCompleted],
  )

  const debouncedSaveProgress = useCallback(
    (progressSeconds: number, totalSeconds: number) => {
      const diff = Math.abs(progressSeconds - lastSavedProgressRef.current)

      if (diff < 5) {
        return
      }

      // タイマーが既にセット済みの場合は、クリアせずに待つ
      if (saveTimeoutRef.current) {
        return
      }

      saveTimeoutRef.current = setTimeout(() => {
        // 即座に lastSavedProgressRef を更新（API完了を待たない）
        lastSavedProgressRef.current = progressSeconds
        saveProgress(progressSeconds, totalSeconds)
        saveTimeoutRef.current = null
      }, PROGRESS_SAVE_DEBOUNCE)
    },
    [saveProgress],
  )

  /** 保留中の保存を即時確定させる（一時停止・アプリ離脱時に位置を取りこぼさないため） */
  const flushProgress = useCallback(() => {
    // アンマウント時は React が ref を外した後に呼ばれるため、最後に観測した値へフォールバックする
    const video = playerRef.current
    const time = video ? video.currentTime : lastKnownTimeRef.current
    if (duration <= 0) return
    // 復元待ちの間は先頭付近の位置を書き込んでしまうので保存しない
    if (pendingSeekRef.current !== null) return
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    if (Math.abs(time - lastSavedProgressRef.current) < 1) return
    lastSavedProgressRef.current = time
    void saveProgress(time, duration)
  }, [duration, saveProgress])

  // バックグラウンドに回るとタブが凍結・破棄される（Android Chrome の discard、iOS PWA の kill）。
  // 凍結中は debounce のタイマーも発火しないため、離脱の直前に再生位置を確定保存する
  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState === 'visible') return
      flushProgress()
    }
    document.addEventListener('visibilitychange', handleHidden)
    window.addEventListener('pagehide', flushProgress)
    return () => {
      document.removeEventListener('visibilitychange', handleHidden)
      window.removeEventListener('pagehide', flushProgress)
    }
  }, [flushProgress])

  // 戻るボタンでの離脱は pagehide も visibilitychange も起きないため、
  // アンマウント時に未保存の位置を確定させる
  const flushProgressRef = useRef(flushProgress)
  useEffect(() => {
    flushProgressRef.current = flushProgress
  }, [flushProgress])

  useEffect(() => {
    return () => {
      flushProgressRef.current()
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
    }
  }, [])

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      setPlaybackRate(rate)
      setSearchParams((prev) => {
        prev.set('playbackRate', rate.toString())
        return prev
      })
    },
    [setSearchParams],
  )

  // メディアを組み直したときの復帰先も選択中の速度にしておく
  useEffect(() => {
    const video = playerRef.current
    if (!video) return
    video.defaultPlaybackRate = playbackRate
  }, [playbackRate, isPlayerReady, videoSrc])

  // コンポーネントアンマウント時: タイマークリーンアップ + ビデオ停止
  useEffect(() => {
    const playerInstance = playerRef.current
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      // ビデオ要素を明示的に停止・解放
      if (playerInstance) {
        playerInstance.pause()
        playerInstance.removeAttribute('src')
        playerInstance.load()
      }
    }
  }, [isPlayerReady])

  const handleProgress = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const playedSeconds = event.currentTarget.currentTime
      setCurrentTime(playedSeconds)

      // 復元位置へ到達するまでは保存しない
      // （seek が取りこぼされたときに先頭付近の位置で上書きしてしまうのを防ぐ）
      const pendingSeek = pendingSeekRef.current
      if (pendingSeek !== null) {
        if (Math.abs(playedSeconds - pendingSeek) >= SEEK_MATCH_TOLERANCE_SEC) return
        pendingSeekRef.current = null
      }
      lastKnownTimeRef.current = playedSeconds

      if (duration > 0) {
        debouncedSaveProgress(playedSeconds, duration)
      }

      const remainingTime = duration - playedSeconds
      if (!goNext) return
      setNextPreviewStage(
        nextEpisodePreviewDismissed ? null : resolveNextPreviewStage(remainingTime, nextPreviewThresholds),
      )
    },
    [duration, nextEpisodePreviewDismissed, nextPreviewThresholds, goNext, debouncedSaveProgress],
  )

  const handleDuration = useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    setDuration(event.currentTarget.duration)
  }, [])

  const handleEnded = useCallback(() => {
    setNextPreviewStage(null)
    setNextEpisodePreviewDismissed(false)
    if (duration > 0) {
      saveProgress(duration, duration, true)
    }
    goNext?.()
  }, [duration, saveProgress, goNext])

  const dropdownSections: (RadioSection | ActionSection)[] = [
    {
      type: 'action' as const,
      label: 'Actions',
      items: [
        {
          key: 'info',
          label: 'Open info',
          onClick: onInfoClick,
        },
      ],
    },
  ]

  // 縦向き時: 右スワイプで戻る（漫画ビューアと同様）フック
  // コントロール表示中はシークバーのドラッグと競合するため無効化する
  const { swipeBackRef, swipeBackState } = useSwipeBack({
    onClose,
    enabled: !isLandscape && !controlsVisible && !isMaximized,
  })

  const swipeBackProgress =
    swipeBackState.isSwiping || swipeBackState.isAnimating
      ? Math.min(1, swipeBackState.translateX / window.innerWidth)
      : 0

  return (
    <div ref={swipeBackRef} className="w-full h-screen fixed">
      {/* スワイプバック時の暗い背景（距離に応じて透明に → 裏のProductContainerが見える） */}
      <div
        className="fixed inset-0 z-[-1] bg-black pointer-events-none transition-opacity"
        style={{ opacity: swipeBackState.isSwiping || swipeBackState.isAnimating ? 1 - swipeBackProgress : 1 }}
      />
      <div
        className="w-full h-full bg-background"
        style={{
          transform: swipeBackState.translateX > 0 ? `translateX(${swipeBackState.translateX}px)` : undefined,
          transition: swipeBackState.isAnimating ? `transform ${SWIPE_BACK_ANIMATION_MS}ms ease-out` : undefined,
        }}
      >
        <Header title={title} onClose={onClose} dropdownSections={dropdownSections} />

        <div
          className={`flex items-center justify-center select-none ${
            isMaximized ? 'fixed inset-0 z-50 bg-black' : 'relative w-full bg-background'
          }`}
          style={isMaximized ? undefined : { height: `calc(100vh - ${HEADER_HEIGHT} - ${FOOTER_HEIGHT})` }}
        >
          {(isUrlLoading && !stableVideoUrl) || isProgressLoading || !hasRestoredProgress ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="size-8 text-muted-foreground animate-spin" />
              <div className="text-muted-foreground text-sm">読み込み中...</div>
            </div>
          ) : stableVideoUrl ? (
            <ReactPlayer
              ref={playerRef}
              src={videoSrc ?? undefined}
              playing={playing && !isHolding}
              volume={volume}
              playbackRate={playbackRate}
              width="100%"
              height="100%"
              style={{ maxHeight: '100%' }}
              onTimeUpdate={handleProgress}
              onDurationChange={handleDuration}
              onReady={handleReady}
              onLoadedMetadata={applyPendingSeek}
              onCanPlay={handleCanPlay}
              onSeeked={handleSeeked}
              onEmptied={handleEmptied}
              onWaiting={handleBuffer}
              onPlaying={handleBufferEnd}
              onPlay={() => {
                const internal = playerRef.current
                // 進捗復元前にブラウザが勝手に再生した場合はすぐ停止
                if (!hasRestoredProgress) {
                  internal?.pause()
                  return
                }
                applyPendingSeek()
                setPlaying(true)
              }}
              onPause={() => {
                flushProgress()
                // 先読みを貯めるための一時停止は、再生したい状態のままにする
                if (holdingRef.current) return
                setPlaying(false)
                // 停止中は playing イベントが来ないため、バッファ表示を残さない
                setIsBuffering(false)
              }}
              onEnded={handleEnded}
              onError={handleError}
              playsInline
              preload="metadata"
            />
          ) : (
            <div className="text-muted-foreground">動画URLを取得できませんでした</div>
          )}

          {/* 読み込み失敗時のオーバーレイ（カスタムコントロールより手前に出す） */}
          {hasLoadError && stableVideoUrl && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-50">
              <div className="flex flex-col items-center gap-3 bg-black/70 rounded-2xl px-8 py-6">
                <div className="text-white text-sm text-center">{errorDetail}</div>
                <Button variant="secondary" size="sm" onClick={handleRetry}>
                  再試行
                </Button>
              </div>
            </div>
          )}

          {/* バッファリング中のオーバーレイ */}
          {(isBuffering || isHolding) && !hasLoadError && stableVideoUrl && !isUrlLoading && (
            <BufferingOverlay
              videoRef={playerRef}
              playbackRate={playbackRate}
              getDownloadRate={getDownloadRate}
              canLowerQuality={
                !isOffline &&
                qualityOptions.some(
                  (o) =>
                    o.status === 'ready' && QUALITY_RANK.indexOf(o.quality) > QUALITY_RANK.indexOf(selectedQuality),
                )
              }
            />
          )}

          {/* カスタムコントロール */}
          {stableVideoUrl && !isUrlLoading && (
            <VideoControls
              playing={playing}
              currentTime={currentTime}
              duration={duration}
              onPlayPause={() => {
                // 進捗復元完了前は再生を許可しない（0:00から再生→シーク で二重フェッチになるため）
                if (!hasRestoredProgress) return
                setPlaying((p) => !p)
              }}
              onSeek={handleSeek}
              onSeekPreview={handleSeekPreview}
              onNext={goNextManual}
              onPrev={goPrev}
              onVisibilityChange={setControlsVisible}
              playbackRate={playbackRate}
              onPlaybackRateChange={handlePlaybackRateChange}
              buffered={bufferProgress?.loaded ?? 0}
              qualityOptions={isOffline ? [] : qualityOptions}
              selectedQuality={selectedQuality}
              onQualityChange={handleQualityChange}
              qualityConversion={qualityConversion}
              isMeteredNetwork={!isWifi}
              isMaximized={isMaximized}
              onToggleMaximized={() => setIsMaximized((v) => !v)}
            />
          )}
        </div>

        <Footer
          currentIndex={Math.floor(currentTime)}
          totalImages={Math.floor(duration)}
          onPrev={goPrev}
          onNext={goNextManual}
          isVideoPlayer
        />

        <NextEpisodePreview
          stage={nextPreviewStage}
          nextEpisodeId={nextEpisodeId}
          nextEpisodeTitle={nextEpisodeTitle}
          controlsVisible={controlsVisible}
          currentTime={currentTime}
          duration={duration}
          onNext={() => {
            // 現在のエピソードを読了にしてから次へ遷移
            if (duration > 0) {
              saveProgress(duration, duration, true)
            }
            goNextManual?.()
          }}
          onDismiss={() => {
            // カードを閉じたのは表示が早すぎたから。閉じた位置を学習して次回以降を遅らせる
            const sample = sampleFromDismiss(duration - currentTime)
            if (sample !== null) recordSample(sample)
            setNextPreviewStage(null)
            setNextEpisodePreviewDismissed(true)
          }}
          isLandscape={isLandscape}
        />
      </div>
    </div>
  )
}
